import { observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { once } from 'lodash-es'

/**
 * Which kind of user uploads documents of a type.
 */
export type UploaderUserType = 'Client' | 'Pro'

export const allWellKnownDocumentTypeShortIds = [
  'FINAL_RETURN',
  'FINAL_RETURN_TO_BE_MAILED',
  'DRAFT_RETURN',
  'EXTENSION_CONFIRMATION',
  'E_FILE_CONFIRMATION',
  'PRIOR_YEAR_RETURN',
  'ID',
  'ANNOTATED_SOURCE_DOCUMENT',
  'OTHER',
  'PREP_NOTES',
  'REVIEW_NOTES',
  'EXTENSION_REJECTION_CONFIRMATION',
] as const

/**
 * Short IDs of the document types the app refers to by name.
 */
export type WellKnownDocumentTypeShortId =
  (typeof allWellKnownDocumentTypeShortIds)[number]

export type DocumentTypeProps = ModelProps<DocumentType>

export class DocumentType extends Model<DocumentType> {
  /**
   * Short ID of the document type.
   */
  id = Id.none

  /**
   * The ID the legacy API refers to the type by. Types primed by short ID
   * only receive it once fetched.
   */
  @observable accessor longId = Id.none
  @observable accessor name = ''
  @observable accessor heading = ''
  @observable accessor shortHelpText = ''
  @observable accessor longHelpText = ''
  @observable accessor imageUrl = ''
  @observable accessor previewUrl = ''
  @observable accessor uploaderUserType: UploaderUserType = 'Pro'
  @observable accessor fields: DocumentTypeField[] = []

  static get none() {
    return lazyNone()
  }
}

const lazyNone = once(() =>
  new DocumentType().set({
    name: 'Unknown',
  }),
)

export type DocumentTypeField = {
  id: string
  label: string
  required?: boolean
  description?: string
  sortOrder: number
} & (
  | {
      type: 'Bool' | 'Date'
    }
  | {
      type: 'Text'
      isMulti?: boolean
    }
  | {
      type: 'Numeric'
      isInteger?: boolean
    }
  | {
      type: 'Object' | 'Array'
      fields: DocumentTypeField[]
    }
)
